import { useHasDocuments } from '~/services/company';
import { useAccountingConsents } from '~/services/consents';
import { Consent, ConsentStatus } from '~/types/consent';
import { isDefined } from '~/types/typeGuards';

export type ConnectionStatus =
  | { state: 'loading' }
  | { state: 'connect'; consent: Consent | null }
  | { state: 'connected_waiting'; consent: Consent | null }
  | { state: 'connected_waiting_with_history'; consent: Consent }
  | { state: 'connected'; consent: Consent | null }
  | { state: 'reconnect'; consent: Consent };

const useAccountingConnectionState = (): ConnectionStatus => {
  const consentsQuery = useAccountingConsents();
  const hasDocumentsQuery = useHasDocuments();
  const consents = consentsQuery.data?.toSorted(compareLastRunAtDesc);
  const hasDocuments = hasDocumentsQuery.data;

  // we use hasDocumentsQuery.isSuccess to avoid cache invalidation
  // bring the user to the loading state again
  if (!consents || !hasDocumentsQuery.isSuccess) {
    return { state: 'loading' };
  }

  const authorizedConsent = consents.find((item) => item.status === ConsentStatus.Authorized);

  // Check if there was an old consent that had already synced
  const oldConsentWithHistory = consents.find((c) => isDefined(c.lastRunAt));

  if (authorizedConsent) {
    if (authorizedConsent.lastRunAt && hasDocuments) {
      return { state: 'connected', consent: authorizedConsent };
    }

    // Authorized consent without lastRunAt but with previous sync history and documents
    if (!authorizedConsent.lastRunAt && oldConsentWithHistory && hasDocuments) {
      return { state: 'connected_waiting_with_history', consent: authorizedConsent };
    }

    return { state: 'connected_waiting', consent: authorizedConsent };
  }

  const previouslyAuthorized = consents.find(
    (consent) =>
      (consent.status === ConsentStatus.Pending || consent.status === ConsentStatus.Unauthorized) &&
      (consent.authorizedAt || consent.lastRunAt),
  );
  if (previouslyAuthorized) {
    return {
      state: 'reconnect',
      consent: previouslyAuthorized,
    };
  }

  const unauthorizedConsent = consents.find((consent) => consent.status === ConsentStatus.Unauthorized);
  if (unauthorizedConsent) {
    return {
      state: 'reconnect',
      consent: unauthorizedConsent,
    };
  }

  //if consent exists in pending status and has never been authorized, it means the user
  //clicked on connect and closed the modal without proceeding. We can reuse the same pending consent
  //for following attempts to avoid recreating a different consent for every click
  const pendingConsent = consents.find((consent) => consent.status === ConsentStatus.Pending);
  if (pendingConsent) {
    return {
      state: 'connect',
      consent: pendingConsent,
    };
  }

  return {
    state: 'connect',
    consent: null,
  };
};

const compareLastRunAtDesc = (a: Consent, b: Consent) => {
  if (!a.lastRunAt && !b.lastRunAt) return 0;
  if (!a.lastRunAt) return 1;
  if (!b.lastRunAt) return -1;
  return new Date(b.lastRunAt).getTime() - new Date(a.lastRunAt).getTime();
};

export default useAccountingConnectionState;
